import React, { useEffect, useState } from "react";
import {
  Grid,
  Typography,
  Paper,
  Backdrop,
  CircularProgress,
  Tabs,
  Tab,
  Button,
  useMediaQuery,
} from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { axiosAPI } from "../../Components/AxiosComponents/Axios";

import TextInput from "../../Components/InputComponents/TextInput";
import TextAreaInput from "../../Components/InputComponents/TextAreaInput";
import CommonButton from "../../Components/InputComponents/CommonButton";

import {
  errorNotify,
  infoNotify,
  successNotify,
} from "../../Components/ToastComponent/ToastNotification";
import { useStaffLayout } from "../Context/CandidateLayoutContext";

const Profile = () => {
    const { triggerProfileAPI } = useStaffLayout();
  const decryptedId = localStorage?.getItem("SWA_UserId") || sessionStorage?.getItem("SWA_UserId");
  const navigate = useNavigate();
  const location = useLocation();

  const paginationState = location?.state?.pagination || {
    page: 0,
    rowsPerPage: 25,
  };

  const filterData = location?.state?.filterData || {
    name: "",
    passport_no: "",
    profession: "",
    branch: "",
  };

  // State to collect text input data
  const [staffData, setStaffData] = useState({
    ED_Name: null,
    ED_Dateofbirth: null,
    ED_ContactNumber: null,
    ED_Email: null,
    ED_Address: null,

    ED_Department: null,
    ED_Designation: null,
    ED_Branch: null,
    ED_ReportingPerson: null,
    ED_EmploymentStatus: null,
    ED_Dateofjoin: null,
    ED_Referal: null,
    ED_Remarks: null,
    ED_Salary: null,
    ED_PayFrequency: null,
    ED_MaxLeave: null,
    ED_SickLeave: null,
    ED_BonusInfo: null,

    ED_UserName: null,
    ED_UserStatus: null,
    ED_Role: null,
  });

  const [initialData, setInitialData] = useState(null);

  const [isDisabledTab, setIsDisabledTab] = useState({
    tab0: true,
    tab1: true,
    tab2: true,
  });

  // State for error handling
  const [errors, setErrors] = useState({});

  const [designationList, setDesignationList] = useState([]);
  const [departmentList, setDepartmentList] = useState([]);
  const [branchList, setBranchList] = useState([]);
  const [employmentStatus, setEmploymentStatus] = useState([]);
  const [employeeRoleList, setEmployeeRoleList] = useState([]);
  const [reportingPersonList, setReportingPersonList] = useState([]);
  const [salaryPayFreq, setSalaryPayFreq] = useState([]);

  const getDesignation = async () => {
    try {
      const res = await axiosAPI.get(`/Designation/list`);
      setDesignationList(res?.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const getDepartment = async () => {
    try {
      const res = await axiosAPI.get(`/Department/active/list`);
      setDepartmentList(res?.data?.data || []);
    } catch (err) {
      console.error("Active API failed, retrying without 'active'", err);
      try {
        const resFallback = await axiosAPI.get(`/Department/list`);
        setDepartmentList(resFallback?.data?.data || []);
      } catch (fallbackErr) {
        console.error("Fallback API also failed", fallbackErr);
      }
    }
  };

  const getBranches = async () => {
    try {
      const res = await axiosAPI.get(`/Branch/active/list`);
      setBranchList(res?.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const getEmploymentStatus = async () => {
    try {
      const res = await axiosAPI.get(`/EmpStatus/active/list`);
      setEmploymentStatus(res?.data?.data || []);
    } catch (err) {
      console.error("Active API failed, retrying without 'active'", err);
      try {
        const resFallback = await axiosAPI.get(`/EmpStatus/list`);
        setEmploymentStatus(resFallback?.data?.data || []);
      } catch (fallbackErr) {
        console.error("Fallback API also failed", fallbackErr);
      }
    }
  };

  const getEmployeeRoleList = async () => {
    try {
      const res = await axiosAPI.get(`/UserRole/list/active`);
      setEmployeeRoleList(res?.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const getReportingPersonList = async () => {
    try {
      const res = await axiosAPI.get(`/Employee/reportingstaff`);
      setReportingPersonList(res?.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const getSalaryPayList = async () => {
    try {
      const res = await axiosAPI.get(`/SalaryPayFrequency/list`);
      setSalaryPayFreq(res?.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const getData = async () => {
    try {
      const res = await axiosAPI.get(`/Employee/listbyid/${decryptedId}`);
      const dataFromApi = res.data.data;
      setStaffData(dataFromApi);
      setInitialData(dataFromApi);
    } catch (err) {
      console.error(err);
    }
  };

  const validateForm = () => {
    const mandatoryFields = [
      { field: "ED_Name", label: "Full Name", tab: 0 },
      { field: "ED_Dateofbirth", label: "Date of Birth", tab: 0 },
      { field: "ED_ContactNumber", label: "Contact Number", type: "phone", tab: 0 },
      { field: "ED_Email", label: "Email Address", type: "email", tab: 0 },
      { field: "ED_Designation", label: "Designation", tab: 1 },
      { field: "ED_Department", label: "Department", tab: 1 },
      { field: "ED_Branch", label: "Branch", tab: 1 },
      { field: "ED_EmploymentStatus", label: "Employment Status", tab: 1 },
      { field: "ED_UserName", label: "Username", tab: 3 },
    ];

    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    const phoneRegex = /^[0-9]{20}$/;

    let formErrors = {};
    let firstErrorTab = null;

    // Validate required fields and types
    for (let { field, label, type, tab } of mandatoryFields) {
      const value = staffData[field];

      if (!value) {
        formErrors[field] = `${label} is required.`;
        if (firstErrorTab === null) firstErrorTab = tab;
      } else {
        if (type === "email" && !emailRegex.test(value)) {
          formErrors[field] = `Please enter a valid ${label}.`;
          if (firstErrorTab === null) firstErrorTab = tab;
        }

      

        if (type === "password" && value.length < 8) {
          formErrors[field] = `${label} must have at least 8 characters.`;
          if (firstErrorTab === null) firstErrorTab = tab;
        }
      }
    }

    // Handle Date validation
    if (staffData.ED_Dateofbirth === "Invalid Date") {
      formErrors["ED_Dateofbirth"] = "Date of Birth format is invalid.";
      if (firstErrorTab === null) firstErrorTab = 0;
    }
    if (staffData.ED_Dateofjoin === "Invalid Date") {
      formErrors["ED_Dateofjoin"] = "Date of Joining format is invalid.";
      if (firstErrorTab === null) firstErrorTab = 1;
    }

    setErrors(formErrors);

    if (Object.keys(formErrors).length > 0) {
      if (firstErrorTab !== null) {
        setTabValue(firstErrorTab); // Change tab to first error
      }

      Object.values(formErrors).forEach((error) => {
        errorNotify(error);
      });

      return false;
    }

    return true;
  };

  const tabFields = {
    tab0: [
      "ED_Name",
      "ED_Dateofbirth",
      "ED_ContactNumber",
      "ED_Email",
      "ED_Address",
    ],
    tab1: [
      "ED_Designation",
      "ED_Department",
      "ED_Branch",
      "ED_ReportingPerson",
      "ED_EmploymentStatus",
      "ED_Dateofjoin",
      "ED_Referal",
      "ED_Remarks",
      "ED_Salary",
      "ED_PayFrequency",
      "ED_MaxLeave",
      "ED_SickLeave",
      "ED_BonusInfo",
    ],
    tab2: ["ED_UserName", "ED_UserStatus", "ED_Role"],
  };

  const compareTabs = (newData) => {
    if (!initialData) return;

    const updatedDisabled = { ...isDisabledTab };

    Object.entries(tabFields).forEach(([tabKey, fields]) => {
      const hasChanged = fields.some((field) => {
        const newVal = newData[field] ?? null;
        const oldVal = initialData[field] ?? null;

        return String(newVal) !== String(oldVal);
      });

      updatedDisabled[tabKey] = !hasChanged;
    });

    setIsDisabledTab(updatedDisabled);
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    const updatedData = { ...staffData, [name]: value };

    setStaffData(updatedData);

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));

    compareTabs(updatedData);
  };

  const [isDisabled, setIsDisabled] = useState(false);
  const [loaderOpen, setLoaderOpen] = useState(false);

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    if (validateForm()) {
      axiosAPI
        ?.post(`/Employee/edit/${decryptedId}`, {
          ED_Name: staffData?.ED_Name,
          ED_Email: staffData?.ED_Email,
          ED_ContactNumber: staffData?.ED_ContactNumber,
          ED_Address: staffData?.ED_Address,
          ED_Department: staffData?.ED_Department,
          ED_Designation: staffData?.ED_Designation,
          ED_Dateofbirth: staffData?.ED_Dateofbirth,
          ED_Dateofjoin: staffData?.ED_Dateofjoin,
          ED_Branch: staffData?.ED_Branch,
          ED_ReportingPerson: staffData?.ED_ReportingPerson,
          ED_EmploymentStatus: staffData?.ED_EmploymentStatus,
          ED_Salary: staffData?.ED_Salary,
          ED_PayFrequency: staffData?.ED_PayFrequency,
          ED_BonusInfo: staffData?.ED_BonusInfo,
          ED_MaxLeave: staffData?.ED_MaxLeave,
          ED_SickLeave: staffData?.ED_SickLeave,
          ED_Remarks: staffData?.ED_Remarks,
          ED_Referal: staffData?.ED_Referal,
          ED_Status: staffData?.ED_UserStatus,
          ED_Role: staffData?.ED_UserType,
        })
        .then((res) => {
          setIsDisabled(false);
          setLoaderOpen(false);

          if (res?.data?.success) {
            successNotify("Profile Updated successfully!");
            triggerProfileAPI()
            navigate("/");
          } else {
            infoNotify(res?.data?.message);
          }
        })
        .catch((err) => {
          errorNotify("Something went wrong!");
          setIsDisabled(false);
          setLoaderOpen(false);
        });
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    getData();
    getDesignation();
    getDepartment();
    getBranches();
    getEmploymentStatus();
    getEmployeeRoleList();
    getReportingPersonList();
    getSalaryPayList();
  }, []);

  const [tabValue, setTabValue] = useState(0);

  const [tabs, setTabs] = useState([
    "Personal Information",
    "Employment Details",
    "Login Credentials",
  ]);

  const handleSubChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const isMobile = useMediaQuery("(max-width: 1220px)");

  return (
    <Paper
      sx={{
        maxWidth: `100vw`,
        padding: 2,
        margin: "auto",
        boxShadow: "none",
        overflowX: "auto",
        fontFamily: "Geist, sans-serif",
        minHeight: "100%",
      }}
    >
      {/* Personal Information ========================================================================================================================================================== */}
      <Grid
        container
       
      >
        {/* Main Heading ================================================================================================================================================================== */}

        <Grid item xs={12} sx={{ padding: "5px" }}>
          <Typography
            variant="h5"
            sx={{ fontWeight: 600, fontFamily: "Geist, sans-serif" }}
          >
            Profile
          </Typography>
        </Grid>

        {/*  Full Name */}
        <Grid item xs={12} sm={6} md={6} lg={4} xl={3} sx={{ padding: "5px" }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
          >
            Full Name <span style={{ color: "red" }}> * </span>
          </Typography>
          <TextInput
            label="Full Name"
            placeholder="Enter Full Name"
            name="ED_Name"
            value={staffData?.ED_Name}
            onChange={handleInputChange}
            error={!!errors.ED_Name}
          />
        </Grid>

        {/* Date of Birth */}
        <Grid item xs={12} sm={6} md={6} lg={4} xl={3} sx={{ padding: "5px" }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
          >
            Date of Birth <span style={{ color: "red" }}> * </span>
          </Typography>
          <TextInput
            label="Date of Birth"
            placeholder="Date of Birth"
            name="ED_Dateofbirth"
            value={staffData?.ED_Dateofbirth}
            onChange={handleInputChange}
            error={!!errors?.ED_Dateofbirth}
            type="date"
            dateRestriction="past" // 'none' | 'future' | 'past'
          />
        </Grid>

        {/*  Contact Number */}
        <Grid item xs={12} sm={6} md={6} lg={4} xl={3} sx={{ padding: "5px" }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
          >
            Contact Number <span style={{ color: "red" }}> * </span>
          </Typography>
          <TextInput
            placeholder="Enter Contact Number"
            type="number"
            name="ED_ContactNumber"
            value={staffData?.ED_ContactNumber}
            onChange={handleInputChange}
            error={!!errors?.ED_ContactNumber}
            // allowOnlyNumbers={true}
            maxLength={15}
          />
        </Grid>

        {/* Email */}
        <Grid item xs={12} sm={6} md={6} lg={4} xl={3} sx={{ padding: "5px" }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
          >
            Email <span style={{ color: "red" }}> * </span>
          </Typography>
          <TextInput
            label="Email Address"
            placeholder="Enter Email Address"
            type="email"
            name="ED_Email"
            value={staffData?.ED_Email}
            onChange={handleInputChange}
            error={!!errors?.ED_Email}
          />
        </Grid>

        {/* Address */}
        <Grid item xs={12} sm={6} md={6} lg={4} xl={3} sx={{ padding: "5px" }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
          >
            Address
          </Typography>
          <TextAreaInput
            label="Address"
            placeholder="Enter Address"
            name="ED_Address"
            value={staffData?.ED_Address}
            onChange={handleInputChange}
          />
        </Grid>

        {/* Save - Only visible on last tab */}
      </Grid>

      {/* Buttons ======================================================================================================================================================================= */}
      <Grid container spacing={2} justifyContent={"flex-end"} sx={{ mt: 3 }}>
        {/* Cancel - Always visible */}

        <Grid item>
          <CommonButton
            variant="outlined"
            customColor="#B0BEC5"
            onClick={() => navigate("/")}
          >
            Cancel
          </CommonButton>
        </Grid>
        <Grid item display="flex" justifyContent={"flex-end"}>
          <CommonButton
            variant="contained"
            onClick={handleSubmit}
            disabled={isDisabledTab.tab0}
          >
            {isDisabled ? "Saving..." : "Save"}
          </CommonButton>
        </Grid>

        {/* Loader Backdrop */}
        <Backdrop
          open={loaderOpen}
          sx={{
            zIndex: (theme) => theme.zIndex.drawer + 1,
            color: "#fff",
            backdropFilter: "blur(5px)",
            backgroundColor: "rgba(0, 0, 0, 0.2)",
            flexDirection: "column",
          }}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </Grid>
    </Paper>
  );
};

export default Profile;
